import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import clinicAPI from "../../../services/clinicApi";
import clubSupportAPI from "../../../services/clubSupportApi";
import { useHubBasePath } from "../../../utils/hubPaths";
import { personDisplayName, trainerPersonalChatTitle } from "../../../utils/clientAppUtils";
import { useAdminAppMode } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useToast } from "../../ui";
import { ChatAvatar, ReadTicks } from "./ChatThread";
import { ActTarget, ActivationPageGuide } from "../../../activation";
import clinicStyles from "../clinic.module.css";
import cs from "./chat.module.css";
import md from "./manageDesktop.module.css";

const SWIPE_LEFT_W = 76;
const SWIPE_LEFT_MAX = SWIPE_LEFT_W * 2;
const SWIPE_RIGHT_W = 80;

const SUPPORT_NAME = "Поддержка Бай.Спорт";
const SUPPORT_PREVIEW = "Вопросы по работе платформы и клуба";

function chatInboxDisplayName(name) {
  if (!name) return name;
  return personDisplayName(name) || name;
}

function formatPreviewTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function PinIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M14.579 14.579L11.6316 17.5264L10.7683 16.6631C10.3775 16.2723 10.1579 15.7422 10.1579 15.1894V13.1053L7.21052 10.158L5 9.42111L9.42111 5L10.158 7.21052L13.1053 10.1579L15.1894 10.1579C15.7422 10.1579 16.2722 10.3775 16.6631 10.7683L17.5264 11.6316L14.579 14.579ZM14.579 14.579L19 19"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoreDotsIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}

function InboxRow({
  to,
  state,
  photoUrl,
  name,
  preview,
  time,
  unread,
  club,
  group,
  navLink = false,
  lastIsMine = false,
  lastIsRead = false,
  threadKey = null,
  isPinned = false,
  folder = "inbox",
  enableSwipe = false,
  swipeDir = null,
  onSwipeOpen,
  onSwipeClose,
  onArchive,
  onUnarchive,
  onDeleteRequest,
  onPin,
  onUnpin,
  onContextMenu,
}) {
  const frontRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const dragging = useRef(false);
  const axisLocked = useRef(null);
  const longPressTimer = useRef(null);
  const suppressClick = useRef(false);
  const [offset, setOffset] = useState(0);
  const [draggingNow, setDraggingNow] = useState(false);
  const offsetRef = useRef(0);
  offsetRef.current = offset;

  const allowPinSwipe = folder === "inbox";
  const minOffset = -SWIPE_LEFT_MAX;
  const maxOffset = allowPinSwipe ? SWIPE_RIGHT_W : 0;

  const syncOffsetFromDir = (dir) => {
    if (dir === "left") return -SWIPE_LEFT_MAX;
    if (dir === "right") return SWIPE_RIGHT_W;
    return 0;
  };

  useEffect(() => {
    if (!enableSwipe) {
      setOffset(0);
      return;
    }
    setOffset(syncOffsetFromDir(swipeDir));
  }, [swipeDir, enableSwipe]);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const menuPayload = (x, y) => ({
    threadKey,
    name,
    isPinned,
    x,
    y,
  });

  useEffect(() => {
    const el = frontRef.current;
    if (!el || !enableSwipe || !threadKey) return undefined;

    const onMove = (e) => {
      if (!dragging.current) return;
      const t = e.touches[0];
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;

      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) clearLongPress();

      if (!axisLocked.current) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axisLocked.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axisLocked.current === "y") {
          dragging.current = false;
          setDraggingNow(false);
          return;
        }
      }
      if (axisLocked.current !== "x") return;
      e.preventDefault();
      clearLongPress();
      const base = syncOffsetFromDir(swipeDir);
      const next = Math.min(maxOffset, Math.max(minOffset, base + dx));
      offsetRef.current = next;
      setOffset(next);
    };

    el.addEventListener("touchmove", onMove, { passive: false });
    return () => el.removeEventListener("touchmove", onMove);
  }, [enableSwipe, threadKey, swipeDir, minOffset, maxOffset]);

  const onTouchStart = (e) => {
    if (!threadKey) return;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    axisLocked.current = null;

    if (enableSwipe) {
      dragging.current = true;
      setDraggingNow(true);
    }

    if (onContextMenu) {
      clearLongPress();
      longPressTimer.current = setTimeout(() => {
        suppressClick.current = true;
        dragging.current = false;
        setDraggingNow(false);
        onSwipeClose?.();
        setOffset(0);
        onContextMenu(menuPayload(t.clientX, t.clientY));
      }, 520);
    }
  };

  const onTouchEnd = () => {
    clearLongPress();
    if (!enableSwipe) {
      setDraggingNow(false);
      return;
    }
    setDraggingNow(false);
    if (!dragging.current && axisLocked.current !== "x") {
      dragging.current = false;
      return;
    }
    dragging.current = false;
    const current = offsetRef.current;
    let dir = null;
    if (current <= -SWIPE_LEFT_MAX / 2) dir = "left";
    else if (allowPinSwipe && current >= SWIPE_RIGHT_W / 2) dir = "right";
    setOffset(syncOffsetFromDir(dir));
    if (dir) onSwipeOpen?.(threadKey, dir);
    else onSwipeClose?.();
  };

  const handleContextMenu = (e) => {
    if (!threadKey || !onContextMenu) return;
    e.preventDefault();
    onContextMenu(menuPayload(e.clientX, e.clientY));
  };

  const handleMoreClick = (e) => {
    if (!threadKey || !onContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    onContextMenu(menuPayload(rect.right, rect.top + rect.height / 2));
  };

  const body = (
    <>
      <ChatAvatar
        photoUrl={photoUrl}
        name={name}
        club={club}
        group={group}
        className={club ? cs.chatInboxAvatarClub : cs.chatInboxAvatar}
        imgClassName={cs.chatInboxAvatarImg}
      />
      <span className={cs.chatInboxBody}>
        <span className={cs.chatInboxTop}>
          <span className={cs.chatInboxName}>
            {isPinned ? (
              <span className={cs.chatInboxPinMark} title="Закреплён">
                <PinIcon />
              </span>
            ) : null}
            {name}
          </span>
          <span className={cs.chatInboxTime}>{formatPreviewTime(time)}</span>
        </span>
        <span className={cs.chatInboxPreviewRow}>
          {lastIsMine ? <ReadTicks read={Boolean(lastIsRead)} /> : null}
          <span className={cs.chatInboxPreview}>{preview || "Нет сообщений"}</span>
        </span>
      </span>
      {unread > 0 ? (
        <span className={cs.chatInboxBadge}>{unread > 99 ? "99+" : unread}</span>
      ) : null}
    </>
  );

  const linkClassName = ({ isActive } = {}) =>
    isActive
      ? `${cs.chatInboxRow} ${cs.chatInboxRowActive}`
      : cs.chatInboxRow;

  const closeIfSwiped = (e) => {
    if (enableSwipe && (swipeDir || Math.abs(offset) > 8)) {
      e.preventDefault();
      onSwipeClose?.();
      setOffset(0);
      return;
    }
  };

  const front =
    navLink ? (
      <NavLink
        to={to}
        state={state}
        className={linkClassName}
        onContextMenu={handleContextMenu}
        onClick={closeIfSwiped}
      >
        {body}
      </NavLink>
    ) : (
      <Link
        to={to}
        state={state}
        className={cs.chatInboxRow}
        onContextMenu={handleContextMenu}
        onClick={closeIfSwiped}
      >
        {body}
      </Link>
    );

  if (!enableSwipe || !threadKey) {
    const showMoreBtn = Boolean(threadKey && onContextMenu);
    return (
      <li className={cs.chatInboxItem} onContextMenu={handleContextMenu}>
        <div className={cs.chatInboxRowWrap}>
          {front}
          {showMoreBtn ? (
            <button
              type="button"
              className={cs.chatInboxMoreBtn}
              aria-label={`Действия: ${name}`}
              title="Действия"
              onClick={handleMoreClick}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MoreDotsIcon />
            </button>
          ) : null}
        </div>
      </li>
    );
  }

  const archiveLabel = folder === "archive" ? "Вернуть" : "Архив";
  const archiveAction = folder === "archive" ? onUnarchive : onArchive;
  const pinLabel = isPinned ? "Открепить" : "Закрепить";
  const pinAction = isPinned ? onUnpin : onPin;

  const showLeftActions = allowPinSwipe && offset > 8;
  const showRightActions = offset < -8;

  return (
    <li className={cs.chatInboxItem}>
      <div className={cs.chatInboxSwipe}>
        {allowPinSwipe ? (
          <div
            className={
              showLeftActions
                ? `${cs.chatInboxSwipeActionsLeft} ${cs.chatInboxSwipeActionsVisible}`
                : cs.chatInboxSwipeActionsLeft
            }
            aria-hidden={!showLeftActions}
          >
            <button
              type="button"
              className={cs.chatInboxSwipePin}
              tabIndex={showLeftActions ? 0 : -1}
              onClick={() => pinAction?.(threadKey)}
            >
              {pinLabel}
            </button>
          </div>
        ) : null}
        <div
          className={
            showRightActions
              ? `${cs.chatInboxSwipeActions} ${cs.chatInboxSwipeActionsVisible}`
              : cs.chatInboxSwipeActions
          }
          aria-hidden={!showRightActions}
        >
          <button
            type="button"
            className={cs.chatInboxSwipeArchive}
            tabIndex={showRightActions ? 0 : -1}
            onClick={() => archiveAction?.(threadKey)}
          >
            {archiveLabel}
          </button>
          <button
            type="button"
            className={cs.chatInboxSwipeDelete}
            tabIndex={showRightActions ? 0 : -1}
            onClick={() => onDeleteRequest?.(menuPayload())}
          >
            Удалить
          </button>
        </div>
        <div
          ref={frontRef}
          className={cs.chatInboxSwipeFront}
          style={{
            transform: `translateX(${offset}px)`,
            transition: draggingNow ? "none" : "transform 0.18s ease",
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          onContextMenu={handleContextMenu}
          onClickCapture={(e) => {
            if (suppressClick.current) {
              e.preventDefault();
              e.stopPropagation();
              suppressClick.current = false;
            }
          }}
        >
          {front}
        </div>
      </div>
    </li>
  );
}

/** Desktop: popover at cursor. Mobile: bottom sheet only when needed (pin). */
function InboxActionsMenu({
  open,
  isMobile,
  folder,
  thread,
  busy,
  onClose,
  onPin,
  onUnpin,
  onArchive,
  onUnarchive,
  onDelete,
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) setConfirmDelete(Boolean(thread?.confirmDelete));
  }, [open, thread?.threadKey, thread?.confirmDelete]);

  useLayoutEffect(() => {
    if (!open || isMobile || !thread) return;
    const menuW = 220;
    const menuH = 230;
    let left = thread.x ?? 24;
    let top = thread.y ?? 24;
    if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
    if (top + menuH > window.innerHeight - 8) top = window.innerHeight - menuH - 8;
    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, [open, isMobile, thread]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointer = (e) => {
      if (busy) return;
      if (menuRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, isMobile, busy, onClose]);

  if (!open || !thread) return null;

  const itemClass = (danger) => {
    if (isMobile) {
      return danger
        ? `${clinicStyles.postActionSheetBtn} ${clinicStyles.postActionSheetBtnDanger}`
        : clinicStyles.postActionSheetBtn;
    }
    return danger
      ? `${clinicStyles.postActionPopoverItem} ${clinicStyles.postActionPopoverItemDanger}`
      : clinicStyles.postActionPopoverItem;
  };

  const items = [];
  if (folder === "inbox") {
    items.push(
      thread.isPinned
        ? { key: "unpin", label: "Открепить", onClick: onUnpin }
        : { key: "pin", label: "Закрепить", onClick: onPin },
    );
    items.push({ key: "archive", label: "В архив", onClick: onArchive });
  } else {
    items.push({ key: "unarchive", label: "Вернуть в чаты", onClick: onUnarchive });
  }
  items.push({
    key: "delete",
    label: "Удалить",
    onClick: () => setConfirmDelete(true),
    danger: true,
  });

  const menuBody = confirmDelete ? (
    <>
      <button
        type="button"
        role={isMobile ? undefined : "menuitem"}
        className={itemClass(true)}
        onClick={onDelete}
        disabled={busy}
      >
        {busy ? "Удаляем…" : "Удалить"}
      </button>
      {!isMobile ? (
        <button
          type="button"
          role="menuitem"
          className={itemClass(false)}
          onClick={onClose}
          disabled={busy}
        >
          Отмена
        </button>
      ) : null}
    </>
  ) : (
    items.map((item) => (
      <button
        key={item.key}
        type="button"
        role={isMobile ? undefined : "menuitem"}
        className={itemClass(item.danger)}
        onClick={item.onClick}
        disabled={busy}
      >
        {item.label}
      </button>
    ))
  );

  if (!isMobile) {
    return createPortal(
      <div
        ref={menuRef}
        className={clinicStyles.postActionPopover}
        style={{ top: pos.top, left: pos.left, right: "auto" }}
        role="menu"
        aria-label={`Действия: ${thread.name}`}
      >
        {menuBody}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={clinicStyles.postActionSheetOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={`Действия: ${thread.name}`}
      onClick={() => !busy && onClose()}
    >
      <div
        className={clinicStyles.postActionSheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={clinicStyles.postActionSheetHandle} aria-hidden />
        <p className={cs.chatInboxActionTitle}>{thread.name}</p>
        {menuBody}
        <button
          type="button"
          className={`${clinicStyles.postActionSheetBtn} ${clinicStyles.postActionSheetBtnCancel}`}
          onClick={onClose}
          disabled={busy}
        >
          Отмена
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function ChatInboxHub({ portal = "staff", embedded = false }) {
  const hubBase = useHubBasePath();
  const clientBase = "/app/chat";
  const { tenant } = useSelector((state) => state.auth);
  const toast = useToast();
  const [threads, setThreads] = useState([]);
  const [supportUnread, setSupportUnread] = useState(0);
  const [archiveUnread, setArchiveUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState("inbox");
  const [menuThread, setMenuThread] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [swipeState, setSwipeState] = useState(null); // { key, dir }
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isManageDesktop = portal === "staff" && isAdminApp && !isMobile;
  const isSidebar = embedded || isManageDesktop;
  const useNavLinks = embedded;
  // Свайп на телефоне/таче; на десктопе — ПКМ
  const enableSwipe = isMobile;

  const patchInboxState = useCallback(
    async (threadKey, action) => {
      if (portal === "client") {
        return clinicAPI.patchMyChatInboxState({ thread_key: threadKey, action });
      }
      return clinicAPI.patchStaffChatInboxState({ thread_key: threadKey, action });
    },
    [portal],
  );

  const load = useCallback(async () => {
    try {
      const supportPromise = clubSupportAPI
        .getSupportUnread()
        .catch(() => ({ unread: 0 }));
      if (portal === "client") {
        const [data, supportData] = await Promise.all([
          clinicAPI.getClientChatInbox({ folder }),
          supportPromise,
        ]);
        const rawThreads = (data.threads || []).map((t) => {
          if (t.thread_type !== "direct") return t;
          return { ...t, client_name: t.client_name || "Команда клуба" };
        });
        setThreads(rawThreads);
        setSupportUnread(supportData.unread || 0);
        setArchiveUnread(Number(data.archive_unread) || 0);
      } else {
        const [data, supportData] = await Promise.all([
          clinicAPI.getStaffChatInbox({ folder }),
          supportPromise,
        ]);
        setThreads(data.threads || []);
        setSupportUnread(supportData.unread || 0);
        setArchiveUnread(Number(data.archive_unread) || 0);
      }
    } catch {
      setThreads([]);
      setSupportUnread(0);
      setArchiveUnread(0);
    } finally {
      setLoading(false);
    }
  }, [portal, folder]);

  useEffect(() => {
    setLoading(true);
    setSwipeState(null);
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 2000);
    const onActivity = () => load();
    window.addEventListener("baysport:chat-activity", onActivity);
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:chat-activity", onActivity);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [load]);

  const runAction = async (threadKey, action) => {
    if (!threadKey) return;
    setActionBusy(true);
    try {
      await patchInboxState(threadKey, action);
      setMenuThread(null);
      setSwipeState(null);
      if (action === "archive") toast.success("Чат в архиве");
      else if (action === "unarchive") toast.success("Чат возвращён");
      else if (action === "delete") {
        toast.success("Чат удалён — появится снова при новом сообщении");
      }
      else if (action === "pin") toast.success("Чат закреплён");
      else if (action === "unpin") toast.success("Чат откреплён");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось изменить чат");
    } finally {
      setActionBusy(false);
    }
  };

  const requestDelete = (payload) => {
    if (!payload?.threadKey) return;
    setSwipeState(null);
    setMenuThread({ ...payload, confirmDelete: true });
  };

  const title = portal === "client" ? "Сообщения" : "Чат";
  const rowProps = useNavLinks ? { navLink: true } : {};

  const supportTo =
    portal === "client" ? `${clientBase}/support` : `${hubBase}/chat/support`;

  const archiveBadge = archiveUnread;

  const folderTabs = (
    <div className={cs.chatInboxTabs} role="tablist" aria-label="Папки чатов">
      <button
        type="button"
        role="tab"
        aria-selected={folder === "inbox"}
        className={
          folder === "inbox"
            ? `${cs.chatInboxTab} ${cs.chatInboxTabActive}`
            : cs.chatInboxTab
        }
        onClick={() => setFolder("inbox")}
      >
        Все
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={folder === "archive"}
        className={
          folder === "archive"
            ? `${cs.chatInboxTab} ${cs.chatInboxTabActive}`
            : cs.chatInboxTab
        }
        onClick={() => setFolder("archive")}
      >
        Архив
        {archiveBadge > 0 ? (
          <span className={cs.chatInboxTabBadge}>
            {archiveBadge > 99 ? "99+" : archiveBadge}
          </span>
        ) : null}
      </button>
    </div>
  );

  const makeActionProps = (threadKey) => ({
    threadKey: threadKey || null,
    folder,
    enableSwipe,
    swipeDir: swipeState?.key === threadKey ? swipeState.dir : null,
    onSwipeOpen: (key, dir) => setSwipeState({ key, dir }),
    onSwipeClose: () => setSwipeState(null),
    onArchive: (key) => runAction(key, "archive"),
    onUnarchive: (key) => runAction(key, "unarchive"),
    onDeleteRequest: requestDelete,
    onPin: (key) => runAction(key, "pin"),
    onUnpin: (key) => runAction(key, "unpin"),
    onContextMenu: (payload) => {
      setSwipeState(null);
      setMenuThread(payload);
    },
  });

  const makeRowProps = (thread) => ({
    ...makeActionProps(thread.thread_key),
    isPinned: Boolean(thread.is_pinned),
  });

  const supportRow =
    folder === "inbox" ? (
      <InboxRow
        key="platform-support"
        to={supportTo}
        name={SUPPORT_NAME}
        preview={SUPPORT_PREVIEW}
        unread={supportUnread}
        club
        {...rowProps}
      />
    ) : null;

  const listBody = loading ? (
    <div className={cs.chatInboxLoading}>
      <div className={cs.chatLoadingDots}>
        <span />
        <span />
        <span />
      </div>
    </div>
  ) : threads.length === 0 ? (
    <ul className={`${cs.chatInboxList} chatInboxList`}>
      {supportRow}
      <li className={cs.chatInboxEmptyHint}>
        {folder === "archive"
          ? isMobile
            ? "Архив пуст. Свайпните чат влево и нажмите «Архив»."
            : "Архив пуст. ПКМ по чату или ⋯ → В архив."
          : "Пока нет переписок"}
      </li>
    </ul>
  ) : (
    <ul className={`${cs.chatInboxList} chatInboxList`}>
      {supportRow}
      {threads.map((thread) => {
        const readProps = {
          lastIsMine: Boolean(thread.last_is_mine),
          lastIsRead: Boolean(thread.last_is_read),
        };
        const keyProps = makeRowProps(thread);

        if (thread.thread_type === "club") {
          const clubTo =
            portal === "client" ? `${clientBase}/club` : `${hubBase}/chat/club`;
          return (
            <InboxRow
              key="club"
              to={clubTo}
              club
              name={thread.client_name || "Чат клуба"}
              preview={thread.last_body}
              time={thread.last_at}
              unread={thread.unread_count}
              {...readProps}
              {...rowProps}
              {...keyProps}
            />
          );
        }

        if (thread.thread_type === "group") {
          const groupTo =
            portal === "client"
              ? `${clientBase}/group/${thread.group_id}`
              : `${hubBase}/chat/group/${thread.group_id}`;
          return (
            <InboxRow
              key={thread.group_id}
              to={groupTo}
              state={{ name: thread.client_name }}
              group
              name={thread.client_name}
              preview={thread.last_body || "Чат тренировочной группы"}
              time={thread.last_at}
              unread={thread.unread_count}
              {...readProps}
              {...rowProps}
              {...keyProps}
            />
          );
        }

        if (portal === "client" && thread.thread_type === "member") {
          const isStaffPeer = ["owner", "admin", "trainer"].includes(
            thread.peer_role,
          );
          const displayName = isStaffPeer
            ? trainerPersonalChatTitle(thread.client_name)
            : chatInboxDisplayName(thread.client_name);
          return (
            <InboxRow
              key={thread.peer_id}
              to={`${clientBase}/member/${thread.peer_id}`}
              state={{ name: thread.client_name }}
              photoUrl={thread.client_photo_url}
              name={displayName}
              preview={thread.last_body || "Личные сообщения"}
              time={thread.last_at}
              unread={thread.unread_count}
              {...readProps}
              {...rowProps}
              {...keyProps}
            />
          );
        }

        if (portal === "client" && thread.thread_type === "direct") {
          const personalQs = thread.athlete_id
            ? `?athlete_id=${encodeURIComponent(thread.athlete_id)}`
            : "";
          return (
            <InboxRow
              key={
                thread.athlete_id ? `direct-${thread.athlete_id}` : "direct"
              }
              to={`${clientBase}/personal${personalQs}`}
              state={{
                name: thread.client_name,
                athleteId: thread.athlete_id || undefined,
              }}
              name={thread.client_name || "Команда клуба"}
              preview={thread.last_body || "Сообщения команде клуба"}
              time={thread.last_at}
              unread={thread.unread_count}
              {...readProps}
              {...rowProps}
              {...keyProps}
            />
          );
        }

        if (portal === "staff" && thread.thread_type === "member") {
          const isStaffPeer = ["owner", "admin", "trainer"].includes(
            thread.peer_role,
          );
          const displayName = isStaffPeer
            ? trainerPersonalChatTitle(thread.client_name) || thread.client_name
            : chatInboxDisplayName(thread.client_name);
          return (
            <InboxRow
              key={`member-${thread.peer_id}`}
              to={`${hubBase}/chat/member/${thread.peer_id}`}
              state={{ name: displayName }}
              photoUrl={thread.client_photo_url}
              name={displayName}
              preview={thread.last_body || "Личные сообщения"}
              time={thread.last_at}
              unread={thread.unread_count}
              {...readProps}
              {...rowProps}
              {...keyProps}
            />
          );
        }

        return (
          <InboxRow
            key={thread.client_id}
            to={
              useNavLinks
                ? `${hubBase}/chat/client/${thread.client_id}`
                : `${hubBase}/clients/${thread.client_id}/chat`
            }
            state={{
              fromChatHub: true,
              clientName: chatInboxDisplayName(thread.client_name),
            }}
            photoUrl={thread.client_photo_url}
            name={chatInboxDisplayName(thread.client_name)}
            preview={thread.last_body}
            time={thread.last_at}
            unread={thread.unread_count}
            {...readProps}
            {...rowProps}
            {...keyProps}
          />
        );
      })}
    </ul>
  );

  const actionsMenu = (
    <InboxActionsMenu
      open={Boolean(menuThread)}
      isMobile={isMobile}
      folder={folder}
      thread={menuThread}
      busy={actionBusy}
      onClose={() => !actionBusy && setMenuThread(null)}
      onPin={() => runAction(menuThread?.threadKey, "pin")}
      onUnpin={() => runAction(menuThread?.threadKey, "unpin")}
      onArchive={() => runAction(menuThread?.threadKey, "archive")}
      onUnarchive={() => runAction(menuThread?.threadKey, "unarchive")}
      onDelete={() => runAction(menuThread?.threadKey, "delete")}
    />
  );

  if (isSidebar) {
    return (
      <div className={`${cs.chatHubPage} ${md.desktopChatSidebarInner}`}>
        {embedded ? (
          <header className={md.desktopChatSidebarHead}>
            <h2 className={md.desktopChatSidebarTitle}>{title}</h2>
          </header>
        ) : (
          <header className={cs.chatHubHeaderCompact}>
            <h2 className={cs.chatHubTitle}>{title}</h2>
          </header>
        )}
        {folderTabs}
        {listBody}
        {actionsMenu}
      </div>
    );
  }

  return (
    <div className={`${cs.chatHubPage} chatHubPage`}>
      <header className={cs.chatHubHeader}>
        <h1 className={cs.chatHubTitle}>{title}</h1>
        {portal === "client" && tenant?.name ? (
          <p className={cs.chatHubSub}>{tenant.name}</p>
        ) : null}
        {folderTabs}
      </header>
      <ActivationPageGuide stepIds={["chat"]} />
      <ActTarget stepId="chat" subId="chat_inbox" as="div">
        {listBody}
      </ActTarget>
      {actionsMenu}
    </div>
  );
}
